/**
 * Centralized color tokens for the controladoria-v2 module.
 *
 * Each constant references a documented design-system token from
 * `design-system/tokens.json` whenever an exact equivalent exists. When no
 * exact token exists, the closest semantic token is chosen and the deviation
 * is annotated inline (// deviation:).
 *
 * Rules of thumb when consuming this file:
 *   1. Component <style> blocks should consume CSS variables from
 *      `src/design/_vars.sass` (e.g. `var(--success)`) — they are equivalent
 *      to the tokens listed below and survive theming changes.
 *   2. JS-side maps (status -> color) MUST import from this module instead of
 *      declaring local hex literals. Tests asserting on color values should
 *      import these constants too, not redeclare hex strings.
 *   3. New semantic colors are added here first, never inside `.vue` files.
 */

// ---------------------------------------------------------------------------
// Semantic palette — every hex literal in the module flows from this map.
// Source-of-truth: design-system/tokens.json
// ---------------------------------------------------------------------------

// Brand / status colors (tokens.json: color.themeVars / color.core).
export const COLOR_SUCCESS = '#56AF79' // tokens.json -> color.themeVars.--success / core.green / --primary
export const COLOR_SUCCESS_STRONG = '#3BA272' // deviation: --success darken (closest doc'd: --success)
export const COLOR_SUCCESS_DARKER = '#1F6C3F' // deviation: --success darker (closest doc'd: --success)
export const COLOR_SUCCESS_DARKER_2 = '#2F8056' // deviation: --success darker variant (closest doc'd: --success)
export const COLOR_SUCCESS_BORDER_SOFT = '#C4D6C9' // deviation: --success desaturated (closest doc'd: --success)
export const COLOR_SUCCESS_BORDER_SOFT_2 = '#C4E0CE' // deviation: --success desaturated (closest doc'd: --success)
export const COLOR_SUCCESS_BG_SOFT = '#EEF7F1' // deviation: --success very light (closest doc'd: --success)
export const COLOR_SUCCESS_BG_HOVER = '#F4F9F6' // deviation: --success very light hover (closest doc'd: --success)

export const COLOR_DANGER = '#EB5757' // tokens.json -> color.themeVars.--danger / core.red
export const COLOR_DANGER_STRONG = '#C14646' // deviation: --danger darken (closest doc'd: --danger)
export const COLOR_DANGER_DARKER = '#8F2E2E' // deviation: --danger darker (closest doc'd: --danger)

export const COLOR_WARNING = '#E0AF00' // tokens.json -> color.themeVars.--warning-new / aawzUtilityColors.az-color-warning
export const COLOR_WARNING_DARKER = '#7A5A00' // deviation: --warning-new darker (closest doc'd: --warning-new)

export const COLOR_INFO = '#00A6DE' // tokens.json -> color.chartPalettes.coresGraficoLinha[1]
export const COLOR_INFO_BLUE = '#4A8BD8' // deviation: no semantic info token (closest doc'd: core.blue #3242B9)
export const COLOR_PURPLE = '#6c4f9b' // tokens.json -> color.chartPalettes.coresGraficoLinha[3]

// Neutral / text / border (tokens.json: color.neutralScale + core).
export const COLOR_TEXT_PRIMARY = '#030309' // tokens.json -> core.black / --black
export const COLOR_TEXT_STRONG = '#5B5B5B' // tokens.json -> neutralScale.1100
export const COLOR_TEXT_MUTED = '#8E8E8E' // tokens.json -> neutralScale.900 / --secondary
export const COLOR_NEUTRAL_400 = '#BEBEBE' // tokens.json -> neutralScale.400
export const COLOR_NEUTRAL_300 = '#D9D9D9' // tokens.json -> neutralScale.300
export const COLOR_BORDER_DEFAULT = '#E4E4E4' // tokens.json -> neutralScale.200 / border.color.default
export const COLOR_WHITE = '#FFFFFF' // tokens.json -> core.white / --white

// Backgrounds & soft borders (tokens.json: closest neutral token; deviations annotated).
export const COLOR_BORDER_SOFT = '#F0F0F0' // deviation: between neutralScale.200 and 300 (closest doc'd: neutralScale.200)
export const COLOR_BG_SECTION = '#FBFAF8' // deviation: warm white (closest doc'd: --gray-1 #F8F8F8)
export const COLOR_BG_SECTION_2 = '#F9F7F5' // deviation: warm white (closest doc'd: --gray-1 #F8F8F8)
export const COLOR_BG_HEADER_SOFT = '#EFEEEA' // deviation: warm cream (closest doc'd: --gray-1)
export const COLOR_BORDER_HEADER_SOFT = '#EFEEEB' // deviation: warm cream border (closest doc'd: --gray-1)
export const COLOR_BG_DISABLED_SECTION = '#F0EFEC' // deviation: neutral cream (closest doc'd: --gray-1)
export const COLOR_BORDER_DISABLED_SECTION = '#E0DFDB' // deviation: neutral cream border (closest doc'd: neutralScale.200)
export const COLOR_BG_PROGRESS_TRACK = '#EEE' // deviation: progress track (closest doc'd: --gray-3 #E4E0E6)
export const COLOR_BG_CHIP_NEUTRAL = '#F4F4F4' // tokens.json -> --gray-f4

// ---------------------------------------------------------------------------
// statusDataRoom-aligned tokens (mirror of `src/utils/statusDataRoom.js`).
// Centralised here so .vue files keep the "no hex literals outside this file"
// rule, while still rendering the exact same icon-svg + color used by the
// dataroom and comissões modules (FechamentosList).
// ---------------------------------------------------------------------------
export const COLOR_STATUS_DATAROOM_COMPLETE = '#6FCF97' // statusDataRoom.complete.color
export const COLOR_STATUS_DATAROOM_WARNING = '#D9AA05' // statusDataRoom.warning.color
export const COLOR_STATUS_DATAROOM_ERROR = '#EB5757' // statusDataRoom.error.color
export const COLOR_STATUS_DATAROOM_EMPTY = '#978B6B' // statusDataRoom.empty.color
export const COLOR_STATUS_DATAROOM_AWAIT = '#e0c200' // statusDataRoom.await.color
export const COLOR_STATUS_DATAROOM_AWAIT_VALIDATION = '#D4790D' // statusDataRoom.await_validation.color

// ---------------------------------------------------------------------------
// Engine state machine map (status -> visuals).
// Used by EnginePanel + statusEngine util.
// Icons/colors mirror `statusDataRoom` (used by dataroom + comissões modules)
// so the controladoria-v2 status header reads identically across the platform.
// ---------------------------------------------------------------------------
export const ENGINE_STATUS_VISUALS = {
  nunca: {
    color: COLOR_STATUS_DATAROOM_EMPTY,
    bg: 'rgba(142,142,142,0.08)',
    icon: 'empty',
    label: 'Nunca rodou',
  },
  pendente: {
    color: COLOR_STATUS_DATAROOM_AWAIT_VALIDATION,
    bg: 'rgba(224,175,0,0.12)',
    icon: 'await-validation',
    label: 'Pendente atualização',
  },
  rodando: {
    color: COLOR_STATUS_DATAROOM_AWAIT,
    bg: 'rgba(0,166,222,0.1)',
    icon: 'timer',
    label: 'Rodando...',
  },
  avisos: {
    color: COLOR_STATUS_DATAROOM_WARNING,
    bg: 'rgba(224,175,0,0.12)',
    icon: 'warning-status',
    label: 'Finalizado com avisos',
  },
  sucesso: {
    color: COLOR_STATUS_DATAROOM_COMPLETE,
    bg: 'rgba(59,162,114,0.1)',
    icon: 'check-circle',
    label: 'Finalizado com sucesso',
  },
  erro: {
    color: COLOR_STATUS_DATAROOM_ERROR,
    bg: 'rgba(235,87,87,0.1)',
    icon: 'close-circle',
    label: 'Finalizado com erro',
  },
  versao_fechada: {
    color: COLOR_TEXT_STRONG,
    bg: 'rgba(91,91,91,0.10)',
    icon: 'lock',
    label: 'Versão fechada',
  },
}

// Compact dot color for the MonthHeader engine pills.
// Aligned with statusDataRoom — same colors rendered next to the icon-svg.
export const ENGINE_PILL_DOT_COLORS = {
  nunca: COLOR_STATUS_DATAROOM_EMPTY,
  pendente: COLOR_STATUS_DATAROOM_AWAIT_VALIDATION,
  rodando: COLOR_STATUS_DATAROOM_AWAIT,
  avisos: COLOR_STATUS_DATAROOM_WARNING,
  sucesso: COLOR_STATUS_DATAROOM_COMPLETE,
  erro: COLOR_STATUS_DATAROOM_ERROR,
  versao_fechada: COLOR_TEXT_STRONG,
}

// ---------------------------------------------------------------------------
// Fechamento DRE status map — used by status-pill.
// Icons/colors mirror `statusDataRoom` (used by dataroom + comissões) so each
// per-DRE cell in the closings table reads identically to the FechamentosList
// of comissões and the per-day status icons of the dataroom.
// ---------------------------------------------------------------------------
export const FECHAMENTO_DRE_STATUS_VISUALS = {
  disponibilizado: {
    label: 'Disponível',
    icon: 'check-circle',
    color: COLOR_STATUS_DATAROOM_COMPLETE,
    bg: 'rgba(86,175,121,0.14)',
    border: 'rgba(86,175,121,0.38)',
  },
  processando: {
    label: 'Processando',
    icon: 'timer',
    color: COLOR_STATUS_DATAROOM_AWAIT,
    bg: 'rgba(224,175,0,0.16)',
    border: 'rgba(224,175,0,0.45)',
  },
  nunca: {
    label: 'Nunca executado',
    icon: 'empty',
    color: COLOR_STATUS_DATAROOM_EMPTY,
    bg: COLOR_BG_DISABLED_SECTION,
    border: COLOR_BORDER_DISABLED_SECTION,
  },
  pendente: {
    label: 'Pendente atualização',
    icon: 'await-validation',
    color: COLOR_STATUS_DATAROOM_AWAIT_VALIDATION,
    bg: 'rgba(224,175,0,0.12)',
    border: 'rgba(224,175,0,0.45)',
  },
  erro: {
    label: 'Erro',
    icon: 'close-circle',
    color: COLOR_STATUS_DATAROOM_ERROR,
    bg: 'rgba(193,70,70,0.12)',
    border: 'rgba(193,70,70,0.42)',
  },
  versao_fechada: {
    label: 'Versão fechada',
    icon: 'lock',
    color: COLOR_TEXT_STRONG,
    bg: 'rgba(91,91,91,0.10)',
    border: 'rgba(91,91,91,0.32)',
  },
}

// ---------------------------------------------------------------------------
// Engine-card colors (rodar-meses-lote modal: PJ / FP / Produto / DFC).
// ---------------------------------------------------------------------------
export const ENGINE_CARD_COLORS = {
  pj: COLOR_SUCCESS,
  fp: COLOR_WARNING,
  produto: COLOR_INFO_BLUE,
  dfc: COLOR_PURPLE,
}

// ---------------------------------------------------------------------------
// Importação tipo colors (importar-lote modal: pagamentos / recebimentos / …).
// ---------------------------------------------------------------------------
export const IMPORTACAO_TIPO_COLORS = {
  pagamentos: COLOR_DANGER_STRONG,
  recebimentos: COLOR_SUCCESS_STRONG,
  comissionamento: COLOR_WARNING,
  provisoes: COLOR_INFO_BLUE,
}

// ---------------------------------------------------------------------------
// ImportCard "imported / not imported" visual state.
// ---------------------------------------------------------------------------
export const IMPORT_CARD_STATUS_VISUALS = {
  importado: {
    bg: 'rgba(59,162,114,0.12)',
    color: COLOR_SUCCESS_STRONG,
    icon: 'check-circle',
    label: 'Importado',
    border: COLOR_SUCCESS_BORDER_SOFT_2,
  },
  nao: {
    bg: 'rgba(224,175,0,0.14)',
    color: COLOR_WARNING,
    icon: 'alert-circle-outline',
    label: 'Não importado',
    border: COLOR_BORDER_DEFAULT,
  },
}

// ---------------------------------------------------------------------------
// Dataroom processing status (importação assíncrona via data_room_file).
// Códigos do backend (STATUSES_PROCESSING + 499 "com avisos"):
//   100 processando · 200 processado · 499 com avisos · 400/500 erro.
// Ícones/cores espelham `statusDataRoom` (idem ENGINE_STATUS_VISUALS) para o
// card de importação ler igual ao resto da plataforma.
// ---------------------------------------------------------------------------
export const DATAROOM_STATUS = {
  PROCESSANDO: 100,
  PROCESSADO: 200,
  AVISOS: 499,
  ERRO_CONFIG: 400,
  ERRO_INESPERADO: 500,
}

const DATAROOM_STATUS_VISUAL_DEFAULT = {
  key: 'aguardando',
  color: COLOR_STATUS_DATAROOM_EMPTY,
  icon: 'empty',
  label: 'Aguardando',
}

export const DATAROOM_STATUS_VISUALS = {
  [DATAROOM_STATUS.PROCESSANDO]: {
    key: 'processando',
    color: COLOR_STATUS_DATAROOM_AWAIT,
    icon: 'timer',
    label: 'Processando...',
  },
  [DATAROOM_STATUS.PROCESSADO]: {
    key: 'processado',
    color: COLOR_STATUS_DATAROOM_COMPLETE,
    icon: 'check-circle',
    label: 'Importado',
  },
  [DATAROOM_STATUS.AVISOS]: {
    key: 'avisos',
    color: COLOR_STATUS_DATAROOM_WARNING,
    icon: 'warning-status',
    label: 'Importado com avisos',
  },
  [DATAROOM_STATUS.ERRO_CONFIG]: {
    key: 'erro',
    color: COLOR_STATUS_DATAROOM_ERROR,
    icon: 'close-circle',
    label: 'Erro no processamento',
  },
  [DATAROOM_STATUS.ERRO_INESPERADO]: {
    key: 'erro',
    color: COLOR_STATUS_DATAROOM_ERROR,
    icon: 'close-circle',
    label: 'Erro inesperado',
  },
}

// Resolve o visual de um status de dataroom (null/desconhecido → "Aguardando").
export const dataroomStatusVisual = (status) => {
  if (status === null || status === undefined) {
    return DATAROOM_STATUS_VISUAL_DEFAULT
  }
  return DATAROOM_STATUS_VISUALS[status] || DATAROOM_STATUS_VISUAL_DEFAULT
}

// Status que carregam feedback relevante (avisos e/ou erros) para abrir o modal.
export const DATAROOM_FEEDBACK_STATUSES = [
  DATAROOM_STATUS.AVISOS,
  DATAROOM_STATUS.ERRO_CONFIG,
  DATAROOM_STATUS.ERRO_INESPERADO,
]

// ---------------------------------------------------------------------------
// Month-cell badge colors (publicado / aberto / nao_iniciado).
// ---------------------------------------------------------------------------
export const MONTH_CELL_STATUS_COLORS = {
  publicado: COLOR_WARNING,
  aberto: COLOR_SUCCESS,
  nao_iniciado: COLOR_NEUTRAL_400,
}
